import { Injectable } from '@angular/core';
import { IProductoCarritoData } from '@core/interfaces/entidades/productoCarrito.entidad';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { IProductoLibre } from '@core/interfaces/producto.libre.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { ComandaItemRes } from '@core/interfaces/request-response/comanda.dto';
import {
  buildProductoComandaToProductoCarrito,
  buildProductoToCarritoCompra,
} from '@shared/utils/functions.utils';
import { GestionTiempoItemRes } from '@core/interfaces/request-response/gestion-tiempo.dto.interface';
import { gestionTiempoItemToCarritoProducto } from '@shared/utils/gestionTiempo.utils';

@Injectable({
  providedIn: 'root',
})
export class CarritoCompraService {
  protected carritoCompra = new BehaviorSubject<IProductoCarritoData[]>([]);
  get productosCarritoCompra(): Observable<IProductoCarritoData[]> {
    return this.carritoCompra.asObservable();
  }
  get totalCarrito(): number {
    return this.carritoCompra
      .getValue()
      .reduce((total, producto) => total + producto.precio * producto.cantidad, 0);
  }
  get cantidadProductos(): number {
    return this.carritoCompra.getValue().reduce((total, producto) => total + producto.cantidad, 0);
  }

  /** Cantidad de un producto específico que ya está en el carrito. */
  cantidadDeProducto(id: number): number {
    return this.carritoCompra.getValue().find((producto) => producto.id === id)?.cantidad ?? 0;
  }
  agregarProductoLibre(producto: IProductoLibre) {
    const listaActual = this.carritoCompra.getValue();
    const productoCarrito: IProductoCarritoData = {
      id: null,
      codigo: 'LIBRE-' + Date.now(),
      nombre: producto.nombre,
      stock: 0,
      controlar_stock: false,
      cantidad: producto.cantidad,
      precio: producto.precio,
    };
    this.carritoCompra.next([...listaActual, productoCarrito]);
  }
  /**
   * Agrega un producto al carrito.
   * @returns `true` si se agregó o incrementó; `false` si no había stock disponible.
   */
  agregar(producto: IProductoData, cantidad: number = 1): boolean {
    const listaActual = this.carritoCompra.getValue();
    const productoExistente = listaActual.find((p) => p.id === producto.id);
    const stock = producto.stock_sucursal?.stock ?? 0;
    const controlarStock = producto.stock_sucursal?.controlar_stock ?? false;

    if (productoExistente) {
      // Verifica si hay stock suficiente antes de incrementar la cantidad
      if (productoExistente.cantidad < stock || controlarStock === false) {
        const nuevaLista = listaActual.map((p) => {
          if (p.id === producto.id) {
            return { ...p, cantidad: p.cantidad + 1 };
          }
          return p;
        });
        this.carritoCompra.next(nuevaLista);
        return true;
      }
      // Si no hay stock suficiente, no hace nada
      return false;
    }

    // Si el producto no está en el carrito y hay stock, lo agrega
    if (stock > 0 || controlarStock === false) {
      this.carritoCompra.next([...listaActual, buildProductoToCarritoCompra(producto, cantidad)]);
      return true;
    }

    return false;
  }

  cargarProductosComandas(productos: ComandaItemRes[]) {
    const productosCarrito: IProductoCarritoData[] = productos.map((x) => {
      return buildProductoComandaToProductoCarrito(x);
    });
    this.carritoCompra.next(productosCarrito);
  }

  cargarProductosGestionTiempo(gestionTiempoItem: GestionTiempoItemRes[]) {
    const productoCarrito: IProductoCarritoData[] =
      gestionTiempoItemToCarritoProducto(gestionTiempoItem);
    this.carritoCompra.next(productoCarrito);
  }

  eliminar(producto: IProductoCarritoData) {
    const listaActual = this.carritoCompra.getValue();
    const nuevaLista = listaActual.filter((p) => p.id !== producto.id);
    this.carritoCompra.next(nuevaLista);
  }
  limpiar() {
    this.carritoCompra.next([]);
  }
}
